import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import DettagliArticolo from './DetagliArticolo';
import StoricoEANModal from './StoricoEANModal';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';


// ─── Utility Funkcije ────────────────────────────────────────────────────────


const exportCSV = (data) => {
  if (!data || data.length === 0) return;

  // Dodat 'Reparto' u zaglavlje
  const headers = ['EAN', 'Articolo', 'Q.tà', 'Min Scorta', 'Reparto', 'Stato', 'MM'];
  
  const rows = data.map(r => [
    r.ean ?? '', 
    r.nome ?? '', 
    r.qtapresente ?? 0,
    r.minscorta ?? 0, 
    r.reparto ?? '', // Novo polje
    r.stato ?? '', 
    r.mm ?? ''
  ]);

  // Formatiranje sa navodnicima oko svake vrednosti
  const csv = [headers, ...rows].map(r => 
    r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')
  ).join('\n');

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a'); 
  a.href = URL.createObjectURL(blob);
  a.download = `stock_cco_${new Date().toISOString().slice(0,10)}.csv`; 
  a.click();
};

const exportExcel = async (data) => {
  if (!data || data.length === 0) return;

  try {
    const XLSX = await import('https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs');
    
    const ws = XLSX.utils.json_to_sheet(data.map(r => ({
      'EAN': r.ean ?? '', 
      'Articolo': r.nome ?? '', 
      'Q.tà': r.qtapresente ?? 0,
      'Min Scorta': r.minscorta ?? 0, 
      'Reparto': r.reparto ?? '', // Novo polje
      'Stato': r.stato ?? '', 
      'MM': r.mm ?? ''
    })));

    // Podešavanje širina kolona (wch = width characters)
    const wscols = [
      { wch: 15 }, // EAN
      { wch: 45 }, // Articolo
      { wch: 10 }, // Q.tà
      { wch: 10 }, // Min Scorta
      { wch: 20 }, // Reparto
      { wch: 20 }, // Stato
      { wch: 10 }  // MM
    ];
    ws['!cols'] = wscols;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock CCO');
    XLSX.writeFile(wb, `stock_cco_${new Date().toISOString().slice(0,10)}.xlsx`);
    
  } catch (err) { 
    console.error("Excel Error:", err);
    alert('Errore esportazione in Excel!'); 
  }
};



const exportPDF = (data) => {
  if (!data || data.length === 0) return;

  try {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const now = new Date().toLocaleString('it-IT');

    // --- HEADER ---
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42); 
    doc.text('ORIZONDESK', 14, 20);
    
    doc.setFontSize(14);
    doc.setTextColor(100);
    doc.text('SITUAZIONE STOCK MAGAZZINO CCO', 14, 28);

    doc.setFontSize(9);
    doc.text(`Articoli totali: ${data.length}  |  Data report: ${now}`, 14, 34);

    // --- PRIPREMA PODATAKA ---
    const tableData = data.map(r => [
      r.ean ?? '---',
      r.nome ?? '---',
      r.qtapresente ?? 0,
      r.minscorta ?? 0,
      r.reparto ?? '---', // DODATO REPARTO
      r.stato ?? '---',
      r.mm ? `${r.mm} MM` : '---'
    ]);

    // --- GENERISANJE TABELE ---
    autoTable(doc, {
      startY: 40,
      // DODATO 'Reparto' U ZAGLAVLJE
      head: [['EAN Code', 'Articolo', 'Q.tà', 'Min.', 'Reparto', 'Stato', 'MM']],
      body: tableData,
      theme: 'striped',
      headStyles: { 
        fillColor: [15, 23, 42], 
        fontSize: 9, 
        fontStyle: 'bold',
        halign: 'center' 
      },
      styles: { 
        fontSize: 8, 
        cellPadding: 3, 
        overflow: 'linebreak' 
      },
      columnStyles: {
        0: { cellWidth: 35 }, // EAN
        1: { cellWidth: 'auto' }, // Articolo (fleksibilno)
        2: { cellWidth: 20, halign: 'center', fontStyle: 'bold' }, // Q.tà
        3: { cellWidth: 20, halign: 'center' }, // Min
        4: { cellWidth: 30, halign: 'center' }, // Reparto (fiksna širina)
        5: { cellWidth: 35, halign: 'center' }, // Stato
        6: { cellWidth: 22, halign: 'right' }   // MM
      },
      didParseCell: function(cellData) {
        // Bojenje Q.tà kolone (index 2)
        if (cellData.section === 'body' && cellData.column.index === 2) {
          const rowData = data[cellData.row.index];
          const qty = Number(rowData.qtapresente) || 0;
          const min = Number(rowData.minscorta) || 0;

          if (qty <= 0) {
            cellData.cell.styles.textColor = [239, 68, 68];
          } else if (qty < min) {
            cellData.cell.styles.textColor = [245, 158, 11];
          } else {
            cellData.cell.styles.textColor = [16, 185, 129];
          }
        }
      },
      didDrawPage: function(drawData) {
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
        const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
        
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.setFont('helvetica', 'normal');

        doc.text(`Report generato il: ${now}`, 14, pageHeight - 10);
        const middleText = `OrizonDesk - Pagina ${doc.internal.getNumberOfPages()}`;
        doc.text(middleText, pageWidth / 2, pageHeight - 10, { align: 'center' });
        const url = "https://desk.orizon-aix.com";
        doc.text(url, pageWidth - 14, pageHeight - 10, { align: 'right' });
      }
    });

    doc.save(`Stock_Magazzino_CCO_${new Date().toISOString().slice(0,10)}.pdf`);

  } catch (error) {
    console.error("PDF Export Error:", error);
    alert("Errore tecnico durante la generazione dello Stock PDF.");
  }
};

// ─── Konfiguracija i Helperi ────────────────────────────────────────────────


const COLONNE = [
  { label: 'EAN Code',  key: 'ean',         width: 140, align: 'left'   },
  { label: 'Articolo',  key: 'nome',         width: 240, align: 'left'   },
  { label: 'Q.tà',      key: 'qtapresente',  width: 80,  align: 'center' },
  { label: 'Min',       key: 'minscorta',    width: 70,  align: 'center' },
  { label: 'Stato',     key: 'stato',        width: 140, align: 'center' },
  { label: 'MM',        key: 'mm',           width: 80,  align: 'right'  },
  { label: '',          key: '__detail',     width: 80,  align: 'center', noSort: true },
];


const TABLE_MIN_WIDTH = COLONNE.reduce((acc, c) => acc + c.width, 0);


const DENSITA = { compatta: 'py-2', normale: 'py-4', spaziosa: 'py-6' };


// Colonne filtrabili — tutte le colonne dati di cco_magazzino
const FILTRO_COLONNE = [
  { label: 'EAN',       key: 'ean'        },
  { label: 'Articolo',  key: 'nome'       },
  { label: 'Stato',     key: 'stato'      },
  { label: 'MM',        key: 'mm'         },
  { label: 'Q.tà',      key: 'qtapresente' },
  { label: 'Min Scorta', key: 'minscorta' },
];


const getQtyConfig = (qty, min) => {
  if (qty <= 0)  return { color: 'text-red-500 font-black',    dot: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,.6)]', badge: 'bg-red-500/10 text-red-500 border-red-500/30'              };
  if (qty < min) return { color: 'text-amber-500 font-black',  dot: 'bg-amber-500',                                    badge: 'bg-amber-500/10 text-amber-500 border-amber-500/30'        };
  return                 { color: 'text-emerald-500 font-bold', dot: 'bg-emerald-500',                                  badge: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'  };
};


const getStatoConfig = (stato) => {
  const s = stato?.toLowerCase() || '';
  if (s.includes('pronto'))      return { color: 'text-emerald-500', dot: 'bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,.5)]', label: 'Pronto',          bg: 'bg-emerald-500/10 border-emerald-500/30' };
  if (s.includes('ordine'))      return { color: 'text-amber-500',   dot: 'bg-amber-500',                                                       label: 'In Ordine',       bg: 'bg-amber-500/10 border-amber-500/30'   };
  if (s.includes('riparazione')) return { color: 'text-red-500',     dot: 'bg-red-500',                                                         label: 'In Riparazione',  bg: 'bg-red-500/10 border-red-500/30'       };
  return                                 { color: 'text-slate-400',   dot: 'bg-slate-400',                                                       label: stato || 'N/D',    bg: 'bg-slate-400/10 border-slate-400/30'  };
};


// ─── Pod-komponente ──────────────────────────────────────────────────────────


// ─── ImpostazioniModal — 3 tabova (Colonne | Filtri | Densità) ───────────────
const ImpostazioniModal = ({
  colVisibili, setColVisibili,
  densita, setDensita,
  filtri, setFiltri,
  valoriDistinti,
  onClose,
}) => {
  const [tab, setTab] = useState('colonne'); // 'colonne' | 'filtri' | 'densita'

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const addFiltro = () => {
    if (filtri.length >= 4) return;
    setFiltri(prev => [...prev, { col: '', val: '' }]);
  };

  const removeFiltro = (i) => setFiltri(prev => prev.filter((_, idx) => idx !== i));

  const updateFiltro = (i, field, value) => {
    setFiltri(prev => prev.map((f, idx) =>
      idx === i
        ? { ...f, [field]: value, ...(field === 'col' ? { val: '' } : {}) }
        : f
    ));
  };

  const activeFiltriCount = filtri.filter(f => f.col && f.val).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" />
      <div className="relative w-full max-w-sm bg-card border-2 border-border/60 rounded-3xl shadow-2xl animate-in zoom-in-95 fade-in duration-300 overflow-hidden flex flex-col max-h-[85vh]">
        <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/60 to-transparent shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border/40 shrink-0">
          <div>
            <p className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/50 mb-0.5">Pannello</p>
            <h3 className="text-lg font-black uppercase tracking-tight">Impostazioni</h3>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-muted/60 hover:bg-red-500/10 hover:text-red-500 border border-border/40 flex items-center justify-center transition-all text-muted-foreground"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex shrink-0 border-b border-border/40 px-2 pt-2">
          {[
            { id: 'colonne',  label: 'Colonne',  icon: 'Columns'       },
            { id: 'filtri',   label: `Filtri${activeFiltriCount > 0 ? ` (${activeFiltriCount})` : ''}`, icon: 'Filter' },
            { id: 'densita',  label: 'Densità',  icon: 'AlignJustify'  },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-2 text-[10px] font-black uppercase tracking-widest rounded-t-xl transition-all duration-200 border-b-2
                ${tab === t.id
                  ? 'text-primary border-primary bg-primary/5'
                  : 'text-muted-foreground/50 border-transparent hover:text-muted-foreground'
                }`}
            >
              <Icon name={t.icon} size={11} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Contenuto tab */}
        <div className="flex-1 overflow-y-auto p-6" style={{ scrollbarWidth: 'thin' }}>

          {/* TAB 1: Colonne */}
          {tab === 'colonne' && (
            <div className="flex flex-col gap-2">
              {COLONNE.filter(c => c.key !== '__detail').map(col => (
                <label key={col.key} className="flex items-center justify-between cursor-pointer group">
                  <span className="text-sm font-bold text-foreground/70 group-hover:text-foreground transition-colors">
                    {col.label}
                  </span>
                  <div
                    onClick={() => setColVisibili(prev => ({ ...prev, [col.key]: !prev[col.key] }))}
                    className={`relative w-10 h-5 rounded-full transition-all duration-300 ${
                      colVisibili[col.key] !== false ? 'bg-primary' : 'bg-muted/80 border border-border/60'
                    }`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-300 ${
                      colVisibili[col.key] !== false ? 'left-5' : 'left-0.5'
                    }`} />
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* TAB 2: Filtri — dinamici da cco_magazzino */}
          {tab === 'filtri' && (
            <div className="flex flex-col gap-4">
              <p className="text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/50">
                Aggiungi fino a 4 filtri per colonna e valore.
              </p>

              {filtri.map((f, i) => (
                <div key={i} className="flex flex-col gap-2.5 p-4 rounded-2xl bg-muted/20 border border-border/30">
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-black uppercase tracking-widest text-primary/60">Filtro {i + 1}</span>
                    <button
                      onClick={() => removeFiltro(i)}
                      className="w-6 h-6 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-all"
                    >
                      <Icon name="X" size={11} />
                    </button>
                  </div>

                  {/* Selezione colonna — tutte le colonne di cco_magazzino */}
                  <select
                    value={f.col}
                    onChange={(e) => updateFiltro(i, 'col', e.target.value)}
                    className="w-full bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all"
                  >
                    <option value="">— Seleziona colonna —</option>
                    {FILTRO_COLONNE.map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>

                  {/* Selezione valore — valori distinti dalla tabella */}
                  {f.col && (
                    <select
                      value={f.val}
                      onChange={(e) => updateFiltro(i, 'val', e.target.value)}
                      className="w-full bg-background border border-border/50 rounded-xl px-3 py-2 text-[12px] font-bold outline-none focus:border-primary/50 transition-all"
                    >
                      <option value="">— Tutti —</option>
                      {(valoriDistinti[f.col] || []).map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}

              {/* Aggiungi filtro */}
              {filtri.length < 4 && (
                <button
                  onClick={addFiltro}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl border-2 border-dashed border-border/40 hover:border-primary/40 hover:bg-primary/5 text-muted-foreground/40 hover:text-primary transition-all duration-200 text-[11px] font-black uppercase tracking-widest"
                >
                  <Icon name="Plus" size={14} />
                  Aggiungi filtro
                </button>
              )}

              {/* Reset */}
              {filtri.length > 0 && (
                <button
                  onClick={() => setFiltri([])}
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-2xl bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 text-red-500/70 hover:text-red-500 transition-all text-[11px] font-black uppercase tracking-widest"
                >
                  <Icon name="Trash2" size={12} />
                  Azzera tutti i filtri
                </button>
              )}
            </div>
          )}

          {/* TAB 3: Densità */}
          {tab === 'densita' && (
            <div className="flex flex-col gap-3">
              <p className="text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/50 mb-1">Spaziatura righe</p>
              {Object.keys(DENSITA).map(d => (
                <button
                  key={d}
                  onClick={() => setDensita(d)}
                  className={`flex items-center justify-between px-4 py-3 rounded-2xl border text-sm font-black uppercase tracking-wider transition-all duration-200
                    ${densita === d
                      ? 'bg-primary text-primary-foreground border-primary shadow-lg shadow-primary/20'
                      : 'bg-muted/30 border-border/40 text-muted-foreground hover:border-primary/30'
                    }`}
                >
                  <span>{d.charAt(0).toUpperCase() + d.slice(1)}</span>
                  {densita === d && <Icon name="Check" size={14} />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-muted/40 border-t border-border/40 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary rounded-xl text-[11px] font-black uppercase tracking-widest transition-all"
          >
            Fatto
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── FiltriBadge ─────────────────────────────────────────────────────────────
const FiltriBadge = ({ filtri, onRemove }) => {
  const attivi = filtri.filter(f => f.col && f.val);
  if (attivi.length === 0) return null;
  const getLabelCol = (key) => {
    const found = FILTRO_COLONNE.find(c => c.key === key);
    return found?.label || key;
  };
  return (
    <div className="flex flex-wrap gap-2 px-1 shrink-0">
      {attivi.map((f, i) => (
        <div
          key={i}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 border border-primary/20 text-primary"
        >
          <Icon name="Filter" size={10} />
          <span className="text-[10px] font-black uppercase tracking-widest">
            {getLabelCol(f.col)}: {f.val}
          </span>
          <button
            onClick={() => onRemove(i)}
            className="ml-1 w-4 h-4 rounded-full bg-primary/20 hover:bg-primary/40 flex items-center justify-center transition-all"
          >
            <Icon name="X" size={9} />
          </button>
        </div>
      ))}
    </div>
  );
};


const ExportDropdown = ({ data }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const voci = [
    { label: 'Esporta CSV',   icon: 'FileText', action: () => exportCSV(data)   },
    { label: 'Esporta Excel', icon: 'Sheet',    action: () => exportExcel(data) },
    { label: 'Esporta PDF',   icon: 'FileDown', action: () => exportPDF(data)   },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-4 py-2.5 bg-muted/40 hover:bg-muted/80 border border-border/40 hover:border-primary/30 rounded-2xl text-[11px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all duration-200 shadow-sm"
      >
        <Icon name="Download" size={14} />
        <span className="hidden sm:inline">Esporta</span>
        <Icon name="ChevronDown" size={12} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-48 bg-card border-2 border-border/60 rounded-2xl shadow-2xl overflow-hidden z-20 animate-in fade-in zoom-in-95 duration-150">
          {voci.map(v => (
            <button
              key={v.label}
              onClick={() => { v.action(); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-[12px] font-bold text-foreground/70 hover:text-foreground hover:bg-primary/5 transition-all duration-150 border-b border-border/20 last:border-0"
            >
              <Icon name={v.icon} size={14} className="text-primary/60" />
              {v.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


// ─── Glavna Komponenta ──────────────────────────────────────────────────────


const RicambiContent = () => {
  const [ricambi, setRicambi]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [searchTerm, setSearchTerm]         = useState('');
  const [sortConfig, setSortConfig]         = useState({ key: 'nome', direction: 'asc' });
  const [displayLimit, setDisplayLimit]     = useState(40);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [eanStorico, setEanStorico]         = useState(null);
  const [showSettings, setShowSettings]     = useState(false);
  const [densita, setDensita]               = useState('normale');
  const [colVisibili, setColVisibili]       = useState({});
  const [realtimeStatus, setRealtimeStatus] = useState('connecting');
  const [filtri, setFiltri]                 = useState([]); // [{ col, val }]
  const [valoriDistinti, setValoriDistinti] = useState({});

  const scrollContainerRef = useRef(null);


  const fetchRicambi = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('cco_magazzino').select('*');
      if (error) throw error;
      setRicambi(data || []);

      // Calcola valori distinti per i filtri dropdown — tutte le colonne di FILTRO_COLONNE
      const dist = {};
      FILTRO_COLONNE.forEach(({ key }) => {
        dist[key] = [...new Set((data || []).map(r => r[key]).filter(v => v !== null && v !== undefined && v !== ''))].sort((a, b) => {
          // Sort numerico per colonne numeriche
          const na = Number(a), nb = Number(b);
          if (!isNaN(na) && !isNaN(nb)) return na - nb;
          return String(a).localeCompare(String(b));
        });
      });
      setValoriDistinti(dist);
    } catch (err) {
      console.error('Errore fetch:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);


  // ─── REALTIME ────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchRicambi();

    const channel = supabase
      .channel('cco_magazzino_realtime_v4')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'cco_magazzino' },
        (payload) => {
          const { eventType, new: newRow, old: oldRow } = payload;
          const newEan = newRow?.ean;
          const oldEan = oldRow?.ean;

          setRicambi((prev) => {
            switch (eventType) {
              case 'INSERT':
                if (prev.some(r => r.ean === newEan)) return prev;
                return [newRow, ...prev];
              case 'UPDATE':
                return prev.map(r => r.ean === newEan ? { ...r, ...newRow } : r);
              case 'DELETE':
                return prev.filter(r => r.ean !== oldEan);
              default:
                return prev;
            }
          });

          if (eventType === 'UPDATE') {
            setSelectedItem(prev =>
              prev?.ean === newEan ? { ...prev, ...newRow } : prev
            );
          }
          if (eventType === 'DELETE') {
            setSelectedItem(prev =>
              prev?.ean === oldEan ? null : prev
            );
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED')                            setRealtimeStatus('live');
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR') setRealtimeStatus('error');
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchRicambi]);


  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };


  const allFiltered = ricambi
    .filter(item => {
      // Search globale
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const match = [item.ean, item.nome, item.mm, item.stato].some(v =>
          v?.toString().toLowerCase().includes(s)
        );
        if (!match) return false;
      }

      // Filtri aggiuntivi da Impostazioni
      for (const f of filtri) {
        if (!f.col || !f.val) continue;
        const itemVal = (item[f.col] ?? '').toString().toLowerCase();
        if (!itemVal.includes(f.val.toLowerCase())) return false;
      }

      return true;
    })
    .sort((a, b) => {
      const av = a[sortConfig.key] ?? '', bv = b[sortConfig.key] ?? '';
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });


  const visibleData = allFiltered.slice(0, displayLimit);


  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 120 && displayLimit < allFiltered.length) {
      setDisplayLimit(prev => prev + 30);
    }
  }, [displayLimit, allFiltered.length]);


  useEffect(() => {
    setDisplayLimit(40);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [searchTerm, filtri]);


  const removeFiltro = (i) => {
    setFiltri(prev => prev.filter((_, idx) => idx !== i));
  };


  const colAttive = COLONNE.filter(c => c.key === '__detail' || colVisibili[c.key] !== false);
  const tableMinWidth = colAttive.reduce((acc, c) => acc + c.width, 0);
  const padClass = DENSITA[densita];

  const activeFiltriCount = filtri.filter(f => f.col && f.val).length;

  const realtimeDot = {
    connecting: 'bg-amber-500',
    live:       'bg-emerald-500 animate-pulse shadow-[0_0_8px_#10b981]',
    error:      'bg-red-500',
  }[realtimeStatus];

  const realtimeLabel = {
    connecting: 'Connessione...',
    live:       'Live',
    error:      'Errore Realtime',
  }[realtimeStatus];


  return (
    <>
      <div className="w-full h-screen flex flex-col p-3 md:p-6 animate-in fade-in duration-700 overflow-hidden bg-background">

        {/* ── Header ── */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-4 md:mb-6 px-1 shrink-0">
          <div>
            <div className="flex items-baseline gap-3">
              <h2 className="text-4xl md:text-5xl font-black tracking-[-0.06em] uppercase leading-none text-foreground">
                STOCK<span className="text-primary">CCO</span>
              </h2>
              {!loading && (
                <span className="text-[10px] font-black uppercase tracking-[.25em] text-muted-foreground/40 pb-1">
                  v3.0
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2 md:mt-3">
              <span className="h-[2px] w-6 bg-primary rounded-full" />
              <p className="text-[10px] font-black uppercase tracking-[.35em] text-muted-foreground/50">
                Sistema di Gestione Magazzino
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3 w-full md:w-auto flex-wrap">
            {/* Ricerca */}
            <div className="relative flex-1 min-w-0">
              <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary/60" />
              <input
                type="text"
                placeholder="Cerca articolo, EAN..."
                className="w-full bg-muted/40 border border-border/40 rounded-2xl py-2.5 pl-9 pr-8 outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/10 transition-all text-sm font-semibold text-foreground placeholder:text-muted-foreground/40 shadow-sm"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 hover:text-foreground transition-colors">
                  <Icon name="X" size={13} />
                </button>
              )}
            </div>

            {/* Export */}
            <ExportDropdown data={allFiltered} />

            {/* Impostazioni — badge se filtri attivi */}
            <button
              onClick={() => setShowSettings(true)}
              className={`relative flex items-center gap-2 px-3 md:px-4 py-2.5 border rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 shadow-sm
                ${activeFiltriCount > 0
                  ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20'
                  : 'bg-muted/40 hover:bg-muted/80 border-border/40 hover:border-primary/30 text-muted-foreground hover:text-foreground'
                }`}
            >
              <Icon name={activeFiltriCount > 0 ? 'Filter' : 'Settings2'} size={14} />
              <span className="hidden sm:inline">
                {activeFiltriCount > 0 ? `Filtri (${activeFiltriCount})` : 'Impostazioni'}
              </span>
              {activeFiltriCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-black flex items-center justify-center">
                  {activeFiltriCount}
                </span>
              )}
            </button>

            {/* Aggiorna */}
            <button
              onClick={fetchRicambi}
              disabled={loading}
              className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-2xl text-[11px] font-black uppercase tracking-widest text-primary transition-all duration-200 shadow-sm disabled:opacity-50"
            >
              <Icon name="RefreshCw" size={14} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Aggiorna</span>
            </button>
          </div>
        </div>

        {/* Badge filtri attivi */}
        {activeFiltriCount > 0 && (
          <div className="mb-3 shrink-0">
            <FiltriBadge filtri={filtri} onRemove={removeFiltro} />
          </div>
        )}

        {/* ── Tabella ── */}
        <div className="flex-1 bg-card border border-border/40 rounded-3xl shadow-xl overflow-hidden flex flex-col min-h-0">

          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-auto min-h-0"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(128,128,128,0.2) transparent',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <table
              className="border-collapse"
              style={{ minWidth: tableMinWidth, width: '100%' }}
            >
              <thead className="sticky top-0 z-10">
                <tr className="bg-muted/90 backdrop-blur-sm border-b border-border/50">
                  {colAttive.map((col, i) => (
                    <th
                      key={col.key}
                      onClick={() => !col.noSort && handleSort(col.key)}
                      style={{ width: col.width, minWidth: col.width }}
                      className={`
                        px-4 py-3.5 text-[10px] font-black uppercase tracking-[.18em]
                        transition-colors select-none whitespace-nowrap
                        ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'}
                        ${!col.noSort ? 'cursor-pointer hover:text-primary' : 'cursor-default'}
                        ${sortConfig.key === col.key ? 'text-primary' : 'text-muted-foreground/60'}
                        ${i === 0 ? 'pl-5' : ''}
                        ${i === colAttive.length - 1 ? 'pr-5' : ''}
                      `}
                    >
                      <div className={`inline-flex items-center gap-1.5
                        ${col.align === 'center' ? 'justify-center' : col.align === 'right' ? 'justify-end' : ''}`}
                      >
                        {col.label}
                        {!col.noSort && sortConfig.key === col.key && (
                          <Icon
                            name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                            size={10}
                            className="text-primary"
                          />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={colAttive.length} className="py-40 text-center">
                      <div className="inline-flex flex-col items-center gap-4">
                        <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                        <span className="text-[10px] font-black uppercase tracking-[.3em] text-muted-foreground/40">
                          Caricamento...
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : visibleData.length === 0 ? (
                  <tr>
                    <td colSpan={colAttive.length} className="py-32 text-center">
                      <div className="inline-flex flex-col items-center gap-3">
                        <Icon name="PackageSearch" size={36} className="text-muted-foreground/20" />
                        <span className="text-sm font-bold text-muted-foreground/40">Nessun articolo trovato</span>
                        <button
                          onClick={() => { setSearchTerm(''); setFiltri([]); }}
                          className="text-[10px] font-black uppercase tracking-widest text-primary/60 hover:text-primary transition-colors"
                        >
                          Azzera ricerca e filtri
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : visibleData.map((item, idx) => {
                  const statoCfg = getStatoConfig(item.stato);
                  const qtyCfg  = getQtyConfig(item.qtapresente, item.minscorta);
                  const isEven  = idx % 2 === 0;

                  return (
                    <tr
                      key={item.ean ?? idx}
                      className={`
                        group border-b border-border/[0.15] transition-all duration-200 cursor-default
                        ${isEven
                          ? 'bg-card hover:bg-primary/[0.05]'
                          : 'bg-muted/[0.25] hover:bg-primary/[0.07]'}
                        hover:shadow-[inset_3px_0_0_0_hsl(var(--primary))]
                      `}
                    >
                      {/* EAN */}
                      {colVisibili['ean'] !== false && (
                        <td
                          className={`px-5 ${padClass} whitespace-nowrap`}
                          style={{ width: COLONNE[0].width, minWidth: COLONNE[0].width }}
                        >
                          <span className="font-mono text-[12px] font-bold text-muted-foreground/30 group-hover:text-primary/70 transition-colors duration-200">
                            {item.ean || '---'}
                          </span>
                        </td>
                      )}

                      {/* Nome */}
                      {colVisibili['nome'] !== false && (
                        <td
                          className={`px-4 ${padClass}`}
                          style={{ width: COLONNE[1].width, minWidth: COLONNE[1].width }}
                        >
                          <span className="text-[13px] font-black uppercase tracking-tight text-foreground/75 group-hover:text-foreground transition-colors duration-200 leading-tight">
                            {item.nome || '---'}
                          </span>
                        </td>
                      )}

                      {/* Q.tà */}
                      {colVisibili['qtapresente'] !== false && (
                        <td
                          className={`px-4 ${padClass} text-center`}
                          style={{ width: COLONNE[2].width, minWidth: COLONNE[2].width }}
                        >
                          <div className="flex items-center justify-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${qtyCfg.dot}`} />
                            <span className={`text-[15px] tabular-nums font-black transition-all duration-200 group-hover:scale-110 ${qtyCfg.color}`}>
                              {item.qtapresente ?? 0}
                            </span>
                          </div>
                        </td>
                      )}

                      {/* Min scorta */}
                      {colVisibili['minscorta'] !== false && (
                        <td
                          className={`px-4 ${padClass} text-center`}
                          style={{ width: COLONNE[3].width, minWidth: COLONNE[3].width }}
                        >
                          <span className="text-[12px] font-bold text-muted-foreground/25 tabular-nums">
                            {item.minscorta ?? 0}
                          </span>
                        </td>
                      )}

                      {/* Stato */}
                      {colVisibili['stato'] !== false && (
                        <td
                          className={`px-4 ${padClass} text-center`}
                          style={{ width: COLONNE[4].width, minWidth: COLONNE[4].width }}
                        >
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl border ${statoCfg.bg} transition-all duration-200`}>
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statoCfg.dot}`} />
                            <span className={`text-[9px] font-black uppercase tracking-widest whitespace-nowrap ${statoCfg.color}`}>
                              {statoCfg.label}
                            </span>
                          </div>
                        </td>
                      )}

                      {/* MM */}
                      {colVisibili['mm'] !== false && (
                        <td
                          className={`px-4 ${padClass} text-right`}
                          style={{ width: COLONNE[5].width, minWidth: COLONNE[5].width }}
                        >
                          <span className="font-black text-[12px] tabular-nums text-foreground/20 group-hover:text-primary/50 transition-colors duration-200 whitespace-nowrap">
                            {item.mm ? `${item.mm} ` : '---'}
                          </span>
                        </td>
                      )}

                      {/* Dettaglio */}
                      <td
                        className={`px-3 ${padClass}`}
                        style={{ width: COLONNE[6].width, minWidth: COLONNE[6].width }}
                      >
                        <div className="flex items-center justify-center gap-2.5">
                          {/* Dugme APRI */}
                          <button
                            onClick={() => setSelectedItem(item)}
                            className="
                              inline-flex items-center justify-center gap-1.5
                              w-10 h-10 md:w-auto md:px-5 md:py-2
                              rounded-2xl bg-primary/5 border border-primary/20 text-primary/60
                              hover:bg-primary hover:text-primary-foreground hover:border-primary
                              hover:shadow-[0_0_15px_rgba(var(--primary-rgb),0.4)] hover:scale-105
                              active:scale-95
                              transition-all duration-300 text-[10px] font-black uppercase tracking-[.15em]
                            "
                          >
                            <Icon name="Eye" size={15} strokeWidth={3} />
                            <span className="hidden md:inline">Apri</span>
                          </button>

                          {/* Dugme STORICO */}
                          <button
                            onClick={() => setEanStorico(item)}
                            className="
                              inline-flex items-center justify-center
                              w-10 h-10 md:w-auto md:px-3 md:py-2
                              rounded-2xl bg-muted/30 border border-border/40 text-muted-foreground/40
                              hover:bg-blue-500/10 hover:text-blue-500 hover:border-blue-500/30
                              transition-all duration-200 text-[10px] font-black uppercase tracking-wider
                            "
                          >
                            <Icon name="History" size={14} />
                            <span className="hidden md:inline ml-1.5 text-muted-foreground/40">Storico</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Indicatore paginazione */}
            {displayLimit < allFiltered.length && (
              <div className="py-4 text-center" style={{ minWidth: tableMinWidth }}>
                <span className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/30">
                  Scorri per caricare altri — {allFiltered.length - displayLimit} rimanenti
                </span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="shrink-0 px-4 md:px-6 py-3 bg-muted/50 border-t border-border/50 flex justify-between items-center gap-3">
            <div className="flex items-center gap-3 md:gap-5 min-w-0">
              <div className="flex items-center gap-2 shrink-0">
                <div className={`w-1.5 h-1.5 rounded-full ${realtimeDot}`} />
                <span className="text-[9px] font-black uppercase tracking-[.25em] text-muted-foreground/50 hidden sm:inline">
                  {realtimeLabel}
                </span>
              </div>
              <div className="h-3 w-px bg-border/50 hidden sm:block" />
              <span className="text-[9px] font-black uppercase tracking-[.15em] text-primary/40 hidden sm:inline">
                STOCK-CCO v3.0
              </span>
            </div>

            <div className="flex items-center gap-2 md:gap-4 shrink-0">
              {(searchTerm || activeFiltriCount > 0) && (
                <span className="text-[9px] font-black uppercase tracking-[.15em] text-muted-foreground/40 hidden sm:inline">
                  {allFiltered.length} / {ricambi.length}
                </span>
              )}
              <span className="bg-primary/10 border border-primary/20 text-primary px-3 md:px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm whitespace-nowrap">
                {allFiltered.length} articoli
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Dettaglio */}
      {selectedItem && (
        <DettagliArticolo
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {/* Modal Storico */}
      {eanStorico && (
        <StoricoEANModal
          ean={eanStorico.ean}
          nome={eanStorico.nome}
          onClose={() => setEanStorico(null)}
        />
      )}

      {/* Modal Impostazioni */}
      {showSettings && (
        <ImpostazioniModal
          colVisibili={colVisibili}
          setColVisibili={setColVisibili}
          densita={densita}
          setDensita={setDensita}
          filtri={filtri}
          setFiltri={setFiltri}
          valoriDistinti={valoriDistinti}
          onClose={() => setShowSettings(false)}
        />
      )}
    </>
  );
};


export default RicambiContent;